<script setup lang="ts">
import { useClipboard } from '@vueuse/core';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import CopyPasteIcon from '@/components/base/assets/CopyPasteIcon.vue';
import SendEmailIcon from '@/components/base/assets/SendEmailIcon.vue';
import Modal from '@/components/base/layout/Modal.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useInitialRequest } from '@/stores/initialRequest';
import { gtag } from '@/utils/analytics';

defineProps<{
  showModal: boolean;
}>();

const emit = defineEmits(['close']);

const { origin } = new URL(useInitialRequest().url);
const route = useRoute();
const source = computed(() => `${origin}${route.fullPath}`);
const { copy, copied, isSupported } = useClipboard({ source });

const title = encodeURIComponent('A Custom Mix from Nuts.com');
const message = encodeURIComponent('Check out my custom mix');
const encodedSource = computed(() => encodeURIComponent(source.value));

const handleClose = () => {
  emit('close');
};

const handleShare = (method: string) => {
  gtag('event', 'share_mix', {
    method,
  });
};

const handleCopy = () => {
  copy(source.value);
  handleShare('copy link');
};
</script>

<template>
  <Modal
    :hasControls="false"
    :isOpen="showModal"
    width="md:max-w-md"
    @handle-close="handleClose"
    data-test="share-mix-modal"
    aria-labelledby="share-mix-title"
  >
    <template #header>
      <Header4 id="share-mix-title">Share This Mix Via</Header4>
    </template>
    <template #body>
      <div class="flex flex-wrap items-center justify-between px-6 my-10 gap-y-4">
        <a
          aria-label="Share via email"
          class="flex flex-col items-center justify-center w-20"
          data-test="share-modal-links"
          :href="`mailto:?to=&body=${message}%3A%20${encodedSource}&subject=${title}`"
          @click="handleShare('email')"
        >
          <span class="flex flex-col items-center justify-center bg-black rounded-full w-11 h-11">
            <SendEmailIcon class="text-white" />
          </span>
          <SmallBodyText class="mt-2 text-black">Email</SmallBodyText>
        </a>
        <a
          class="flex flex-col items-center justify-center w-20"
          :href="`https://www.facebook.com/sharer.php?t=${title}&u=${encodedSource}`"
          target="_blank"
          @click="handleShare('facebook')"
        >
          <img src="@/assets/social/facebook-circle.svg" alt="Share via facebook" />
          <SmallBodyText class="mt-2 text-black">Facebook</SmallBodyText>
        </a>
        <a
          class="flex flex-col items-center justify-center w-20"
          :href="`https://pinterest.com/pin/create/button/?url=${encodedSource}&media=https://nuts.com/images/rackcdn/ed910ae2d60f0d25bcb8-80550f96b5feb12604f4f720bfefb46d.ssl.cf1.rackcdn.com/f080d5551b0c49d1-r1VAvsdD-medium.jpg&description=${message} `"
          target="_blank"
          @click="handleShare('pinterest')"
        >
          <img src="@/assets/social/pinterest-circle.svg" alt="Share via pinterest" />
          <SmallBodyText class="mt-2 text-black">Pinterest</SmallBodyText>
        </a>
        <UnstyledButton
          v-if="isSupported"
          class="flex flex-col items-center justify-center w-20 hover:underline"
          @click="handleCopy"
        >
          <span class="sr-only">{{ copied ? 'Link Copied' : 'Share via copy link' }}</span>
          <span class="flex flex-col items-center justify-center bg-black rounded-full w-11 h-11">
            <CopyPasteIcon class="text-white" />
          </span>
          <SmallBodyText class="mt-2">{{ copied ? 'Link Copied!' : 'Copy Link' }}</SmallBodyText>
        </UnstyledButton>
      </div>
    </template>
  </Modal>
</template>
